import { useMemo, useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

function calcEMI(p, r, n) {
  const rm = r / 12 / 100;
  if (rm === 0) return p / n;
  return (p * rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1);
}

export default function EMICalculator() {
  const [amount, setAmount] = useState(150000);
  const [rate, setRate] = useState(16);
  const [months, setMonths] = useState(18);

  const emi = useMemo(() => calcEMI(amount, rate, months), [amount, rate, months]);
  const total = emi * months;
  const interest = total - amount;

  const data = [
    { name: "Principal", value: Math.round(amount) },
    { name: "Interest", value: Math.round(interest) },
  ];
  const COLORS = ["#4F46E5", "#8B5CF6"];

  return (
    <MarketingLayout>
      <section className="page-shell px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">EMI Calculator</div>
          <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05]">Plan your monthly outflow.</h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-md">See exactly how much you'll pay every month — before you apply.</p>
        </div>
        <Card className="lg:col-span-7 p-8 rounded-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2"><Label>Amount</Label><Input value={amount} onChange={(e) => setAmount(Number(e.target.value) || 0)} className="w-32 text-right" data-testid="emi-amount" /></div>
                <Slider value={[amount]} min={10000} max={500000} step={5000} onValueChange={(v) => setAmount(v[0])} />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2"><Label>Rate (p.a.)</Label><Input value={rate} onChange={(e) => setRate(Number(e.target.value) || 0)} className="w-32 text-right" data-testid="emi-rate" /></div>
                <Slider value={[rate]} min={8} max={28} step={0.5} onValueChange={(v) => setRate(v[0])} />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2"><Label>Months</Label><Input value={months} onChange={(e) => setMonths(Number(e.target.value) || 0)} className="w-32 text-right" data-testid="emi-months" /></div>
                <Slider value={[months]} min={3} max={60} step={1} onValueChange={(v) => setMonths(v[0])} />
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="rounded-xl bg-muted p-4"><div className="text-xs">EMI</div><div className="font-display text-2xl">₹{Math.round(emi).toLocaleString("en-IN")}</div></div>
                <div className="rounded-xl bg-muted p-4"><div className="text-xs">Interest</div><div className="font-display text-2xl">₹{Math.round(interest).toLocaleString("en-IN")}</div></div>
                <div className="rounded-xl bg-muted p-4"><div className="text-xs">Total</div><div className="font-display text-2xl">₹{Math.round(total).toLocaleString("en-IN")}</div></div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" paddingAngle={4}>
                    {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </section>
    </MarketingLayout>
  );
}
